import * as React from "react";
import { Button, Modal } from "semantic-ui-react";
import { debounce } from "lodash";
import axios from "axios";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { getEmailDomain } from "@teamprevent/common/customerUser/models";
import { toast } from "@teamprevent/common/toasts";
import { ConfirmedButton } from "@teamprevent/common/confirmedButton/ConfirmedButton";
import { debounceDelay } from "@teamprevent/common/filtering/constants";

import { CREATE_CUSTOMERUSER } from "../actiontypes";

import { CustomerDetailsApi } from "../../../customerDetails/CustomerDetailsApi";

import { CreateUnconnected, CreateProps, DispatchProps } from "./Create";

interface State {
    isEmailWithinAllowedDomains: boolean;
    isEmailDomainSharedAcrossMultipleCustomers: boolean;
}

export class CreateModal extends React.Component<CreateProps & DispatchProps, State> {

    private createForm: CreateUnconnected;
    private customerDetailsApi = new CustomerDetailsApi(axios);

    constructor(props: CreateProps & DispatchProps) {
        super(props);
        this.state = {
            isEmailWithinAllowedDomains: false,
            isEmailDomainSharedAcrossMultipleCustomers: false
        };

        this.onEmailAddressChanged = this.onEmailAddressChanged.bind(this);
    }

    private onEmailAddressChanged(emailAddress: string, valid: boolean) {
        if (valid) {
            const emailDomain = getEmailDomain(emailAddress);
            const isEmailWithinAllowedDomains = this.props.customer.allowedEmailDomains?.includes(emailDomain);

            this.setState({
                ...this.state,
                isEmailWithinAllowedDomains
            });

            this.isEmailDomainSharedAcrossMultipleCustomersDebounced(emailAddress);
        }
    }

    public render() {
        return (
            <Modal size="small" open={this.props.open} onClose={this.props.close}>
                <Modal.Header>Create CustomerUser</Modal.Header>
                <Modal.Content>
                    <CreateUnconnected
                        open={this.props.open}
                        save={this.props.save}
                        ref={instance => this.createForm = instance}
                        customer={this.props.customer}
                        currentUser={this.props.currentUser}
                        allowedAssessmentAndWellBeingFeatures={this.props.allowedAssessmentAndWellBeingFeatures}
                        orgStructures={this.props.orgStructures}
                        close={this.props.close}
                        onEmailAddressChanged={this.onEmailAddressChanged}
                        loadOrgStructures={this.props.loadOrgStructures}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={this.props.close}>Cancel</Button>
                    {this.getSaveButton()}
                </Modal.Actions>
            </Modal>
        );
    }

    private isEmailDomainSharedAcrossMultipleCustomersDebounced = debounce((emailAddress: string) =>
        this.isEmailDomainSharedAcrossMultipleCustomers(emailAddress), debounceDelay);

    private async isEmailDomainSharedAcrossMultipleCustomers(emailAddress: string) {
        const isShared = await this.customerDetailsApi.isEmailDomainSharedAcrossMultipleCustomers(emailAddress);
        this.setState({
            ...this.state,
            isEmailDomainSharedAcrossMultipleCustomers: isShared
        });
    }

    private getSaveButton() {
        return this.state.isEmailDomainSharedAcrossMultipleCustomers
            ? (<ConfirmedButton
                content="Save"
                onClick={() => this.onSubmit()}
                promptText={`This domain is allowable in multiple customers, you are currently setting
                            this user up in ${this.props.customer.name} customer - are you sure you wish to continue?
                            Type 'CREATE' and click confirm to create.`}
                typeToConfirmText="CREATE"
                headingText="Create Customer User"
            />)
            : <IsLoadingButton requestNames={CREATE_CUSTOMERUSER} content="Save" onClick={this.onSubmit} />;
    }

    private onSubmit = () => {
        if (!this.state.isEmailWithinAllowedDomains) {
            toast.warning("Email address must belong to allowed email domains!");
            return;
        }

        this.createForm.submit();
    };
}
